import { useState } from "react";
import { base44, apiClient } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Building2, LayoutGrid, Columns3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import CustomerCard from "@/components/customers/CustomerCard";
import CustomerForm from "@/components/customers/CustomerForm";
import DetailPanel from "@/components/shared/DetailPanel";
import ActivityForm from "@/components/shared/ActivityForm";
import TaskForm from "@/components/tasks/TaskForm";
import PipelineView from "@/components/customers/PipelineView";

export default function BusinessCustomers() {
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [industryFilter, setIndustryFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "pipeline"
  const [confirmDeleteCustomer, setConfirmDeleteCustomer] = useState(null);
  const [confirmClearDemo, setConfirmClearDemo] = useState(false);

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const queryClient = useQueryClient();

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: () => base44.entities.BusinessCustomer.list("-created_date"),
  });

  const { data: activities = [] } = useQuery({
    queryKey: ["activities", selectedCustomer?.id],
    queryFn: () => base44.entities.Activity.filter({ 
      related_to: "business_customer", 
      related_id: selectedCustomer?.id 
    }, "-created_date"),
    enabled: !!selectedCustomer,
  });

  const { data: selectedTasks = [] } = useQuery({
    queryKey: ["tasks", selectedCustomer?.id],
    queryFn: () => base44.entities.Task.filter({ 
      related_to_type: "customer",
      related_to_id: selectedCustomer?.id 
    }, "-created_date"),
    enabled: !!selectedCustomer,
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.BusinessCustomer.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.BusinessCustomer.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setShowForm(false);
      setEditingCustomer(null);
      if (selectedCustomer) {
        setSelectedCustomer(customers.find(c => c.id === selectedCustomer.id));
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => apiClient.delete(`/customers/${id}/cascade`),
    onSuccess: (res) => {
      queryClient.invalidateQueries();
      setSelectedCustomer(null);
      setConfirmDeleteCustomer(null);
      const d = (res && res.data && res.data.deleted) || {};
      const total = Object.values(d).reduce((s, n) => s + n, 0);
      toast.success(`Kunde slettet – ${total} tilknyttede poster fjernet`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke slette kunde"),
  });

  const clearDemoMutation = useMutation({
    mutationFn: () => apiClient.post("/admin/clear-demo-customers"),
    onSuccess: (res) => {
      queryClient.invalidateQueries();
      setConfirmClearDemo(false);
      setSelectedCustomer(null);
      const data = (res && res.data) || {};
      const linked = Object.values(data.deleted || {}).reduce((s, n) => s + n, 0);
      const custDeleted = data.customers_deleted || 0;
      toast.success(`${custDeleted} demokunder slettet – ${Math.max(linked - custDeleted, 0)} tilknyttede poster fjernet`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke slette demokunder"),
  });

  const activityMutation = useMutation({
    mutationFn: (data) => base44.entities.Activity.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      setShowActivityForm(false);
    },
  });

  const createTaskMutation = useMutation({
    mutationFn: (data) => base44.entities.Task.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      setShowTaskForm(false);
      setEditingTask(null);
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Task.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      setShowTaskForm(false);
      setEditingTask(null);
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (id) => base44.entities.Task.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  const handleSubmit = (data) => {
    if (editingCustomer) {
      updateMutation.mutate({ id: editingCustomer.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleStatusChange = (customer, newStatus) => {
    updateMutation.mutate({ 
      id: customer.id, 
      data: { ...customer, status: newStatus } 
    });
  };

  const handleTaskSubmit = (data) => {
    if (editingTask) {
      updateTaskMutation.mutate({ id: editingTask.id, data });
    } else {
      createTaskMutation.mutate(data);
    }
  };

  const handleTaskStatusChange = (task, newStatus) => {
    updateTaskMutation.mutate({ 
      id: task.id, 
      data: { ...task, status: newStatus } 
    });
  };

  const filteredCustomers = customers.filter(customer => {    const matchesSearch = 
      customer.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      customer.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
      customer.contact_email?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = viewMode === "pipeline" || statusFilter === "all" || customer.status === statusFilter;
    const matchesIndustry = industryFilter === "all" || customer.industry === industryFilter;
    return matchesSearch && matchesStatus && matchesIndustry;
  });

  const demoCount = customers.filter(c => c.is_demo).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Bedriftskunder</h1>
            <p className="text-slate-500 mt-1">{customers.length} totalt kunder</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 p-1 bg-white rounded-lg border border-slate-200 shadow-sm">
              <button
                onClick={() => setViewMode("grid")}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "grid"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
                Rutenett
                </button>
                <button
                onClick={() => setViewMode("pipeline")}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "pipeline"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
                >
                <Columns3 className="w-4 h-4" />
                Salgstrakt
              </button>
            </div>
            <Button onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" /> Legg til Kunde
            </Button>
            {isAdmin && demoCount > 0 && (
              <Button
                variant="outline"
                onClick={() => setConfirmClearDemo(true)}
                data-testid="clear-demo-customers-button"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Slett alle demokunder
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        {viewMode === "grid" && (
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Søk kunder..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 bg-white border-0 shadow-sm"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Statuser</SelectItem>
                <SelectItem value="lead">Lead</SelectItem>
                <SelectItem value="prospect">Prospekt</SelectItem>
                <SelectItem value="active">Aktiv</SelectItem>
                <SelectItem value="inactive">Inaktiv</SelectItem>
                <SelectItem value="churned">Tapt</SelectItem>
              </SelectContent>
            </Select>
            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
                <SelectValue placeholder="Industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Bransjer</SelectItem>
                <SelectItem value="technology">Teknologi</SelectItem>
                <SelectItem value="healthcare">Helse</SelectItem>
                <SelectItem value="finance">Finans</SelectItem>
                <SelectItem value="retail">Handel</SelectItem>
                <SelectItem value="manufacturing">Produksjon</SelectItem>
                <SelectItem value="real_estate">Eiendom</SelectItem>
                <SelectItem value="education">Utdanning</SelectItem>
                <SelectItem value="consulting">Konsultering</SelectItem>
                <SelectItem value="other">Annet</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {viewMode === "pipeline" && (
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Søk kunder..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 bg-white border-0 shadow-sm"
              />
            </div>
            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
                <SelectValue placeholder="Industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Bransjer</SelectItem>
                <SelectItem value="technology">Teknologi</SelectItem>
                <SelectItem value="healthcare">Helse</SelectItem>
                <SelectItem value="finance">Finans</SelectItem>
                <SelectItem value="retail">Handel</SelectItem>
                <SelectItem value="manufacturing">Produksjon</SelectItem>
                <SelectItem value="real_estate">Eiendom</SelectItem>
                <SelectItem value="education">Utdanning</SelectItem>
                <SelectItem value="consulting">Konsultering</SelectItem>
                <SelectItem value="other">Annet</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-48 bg-white rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Ingen kunder funnet</h3>
            <p className="text-slate-500 mb-6">Kom i gang ved å legge til din første bedriftskunde</p>
            <Button onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" /> Legg til Kunde
            </Button>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredCustomers.map(customer => (
              <CustomerCard
                key={customer.id}
                customer={customer}
                onClick={() => setSelectedCustomer(customer)}
              />
            ))}
          </div>
        ) : (
          <PipelineView
            customers={filteredCustomers}
            onStatusChange={handleStatusChange}
            onCustomerClick={setSelectedCustomer}
          />
        )}
      </div>

      {/* Forms & Panels */}
      {showForm && (
        <CustomerForm
          customer={editingCustomer}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowForm(false);
            setEditingCustomer(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {selectedCustomer && (
        <DetailPanel
          item={selectedCustomer}
          type="customer"
          activities={activities}
          tasks={selectedTasks}
          onClose={() => setSelectedCustomer(null)}
          onEdit={() => {
            setEditingCustomer(selectedCustomer);
            setShowForm(true);
          }}
          onDelete={() => setConfirmDeleteCustomer(selectedCustomer)}
          onAddActivity={() => setShowActivityForm(true)}
          onAddTask={() => setShowTaskForm(true)}
          onEditTask={(task) => {
            setEditingTask(task);
            setShowTaskForm(true);
          }}
          onDeleteTask={(id) => deleteTaskMutation.mutate(id)}
          onTaskStatusChange={handleTaskStatusChange}
          isDeleting={deleteMutation.isPending}
        />
      )}

      {showActivityForm && selectedCustomer && (
        <ActivityForm
          relatedTo="business_customer"
          relatedId={selectedCustomer.id}
          relatedName={selectedCustomer.company_name}
          onSubmit={(data) => activityMutation.mutate(data)}
          onClose={() => setShowActivityForm(false)}
          isLoading={activityMutation.isPending}
        />
      )}

      {showTaskForm && selectedCustomer && (
        <TaskForm
          task={editingTask}
          relatedTo="customer"
          relatedId={selectedCustomer.id}
          relatedName={selectedCustomer.company_name}
          onSubmit={handleTaskSubmit}
          onClose={() => {
            setShowTaskForm(false);
            setEditingTask(null);
          }}
          isLoading={createTaskMutation.isPending || updateTaskMutation.isPending}
        />
      )}

      <AlertDialog open={!!confirmDeleteCustomer} onOpenChange={(v) => !v && setConfirmDeleteCustomer(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette kunde og all tilknyttet data?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-brand-ink">{confirmDeleteCustomer?.company_name}</span> og
              {" "}alt som er knyttet til kunden – oppkjøpskandidater, prosjekter, oppgaver,
              tidsregistreringer, fakturaer og aktiviteter – slettes permanent. Handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(confirmDeleteCustomer.id)}
            >
              {deleteMutation.isPending ? "Sletter..." : "Slett alt"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmClearDemo} onOpenChange={setConfirmClearDemo}>
        <AlertDialogContent data-testid="clear-demo-customers-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette alle {demoCount} demokunder?</AlertDialogTitle>
            <AlertDialogDescription>
              Alle demokunder og alt som er knyttet til dem – oppkjøpskandidater, prosjekter,
              oppgaver, tidsregistreringer, fakturaer og aktiviteter – slettes permanent i ett grep.
              Kunder du selv har opprettet berøres <span className="font-semibold text-brand-ink">ikke</span>.
              Handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-clear-demo-customers-button"
              className="bg-red-600 hover:bg-red-700"
              disabled={clearDemoMutation.isPending}
              onClick={() => clearDemoMutation.mutate()}
            >
              {clearDemoMutation.isPending ? "Sletter..." : `Slett ${demoCount} demokunder`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}